import {
  Directive,
  DestroyRef,
  ViewContainerRef,
  inject,
  contentChildren,
  Signal,
  input,
  effect,
  InputSignal,
  afterNextRender,
  Injector
} from '@angular/core';
import { Subject } from 'rxjs';
import { FocusTrackerItemDirective } from './focus-tracker-item.directive';

@Directive({
  selector: '[tdWbFocusTracker]',
  exportAs: 'tdWbFocusTracker'
})
export class FocusTrackerDirective {

  /**Used to point to a place in the page, through template variable ref. where the focus should
   * go when there are no more focus tracker items. Caution! only applicable to html elements,
   * not angular components! For components to work, each of them has to be modified to reveal HTMLelement
   * in its template, to be used a default focus. This is not currently implemented.
   * */

  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  private readonly itemsSignal: Signal<ReadonlyArray<FocusTrackerItemDirective>> = contentChildren(FocusTrackerItemDirective, { descendants: true });

  readonly defaultFocus: InputSignal<HTMLElement | undefined> = input<HTMLElement | undefined>(undefined, { alias: 'tdWbDefaultFocusTracker' });
  private previousItemIds: Array<string> = [];
  private resetSubject: Subject<void> = new Subject<void>();

  private getDefaultFocusEl(): HTMLElement {
    return this.defaultFocus() ?? this.viewContainerRef.element.nativeElement;
  }

  constructor() {
    // effect: restores focus when the set of tracked content children changes —
    // afterNextRender + HTMLElement.focus() are imperative browser APIs that
    // cannot be expressed as computed() or linkedSignal().
    effect(() => {
      const previousIds = this.previousItemIds;
      const items = this.itemsSignal();
      const currentIds = items
        .map((item: FocusTrackerItemDirective) => item.trackById())
        .filter((id: string | undefined): id is string => id !== undefined);
      const contentHasChanged = !(this.sameStringSet({ setA: previousIds, setB: currentIds }));
      // If the content has changed, reset the focus
      if (contentHasChanged) {
        this.reset();
        this.setFocus(items as Array<FocusTrackerItemDirective>, previousIds);
        this.previousItemIds = currentIds;
      }
    });

    this.destroyRef.onDestroy(() => {
      this.reset();
    });

  }

  sameStringSet(params: { setA: Array<string>, setB: Array<string> }): boolean {
    if (params.setA.length !== params.setB.length) return false;
    const sa = new Set(params.setA);
    const sb = new Set(params.setB);

    if (sa.size !== sb.size) return false;
    for (const item of sa) {
      if (!sb.has(item)) return false;
    }
    return true;
  }

  private reset(): void {
    if (this.resetSubject !== undefined) {
      this.resetSubject.next();
      this.resetSubject.complete();
      this.resetSubject = new Subject<void>();
    }
  }

  //If no items remain, focus the configured fallback element. If an item was removed, focus the next logical item.
  private setFocus(items: Array<FocusTrackerItemDirective>, previousIds: Array<string>): void {
    if (items.length === 0) {
      const fallback = this.getDefaultFocusEl();
      if (fallback !== undefined && typeof fallback.focus === 'function') {
        afterNextRender(() => fallback.focus(),
          { injector: this.injector }
        );
        return;
      }
    }
    const removedItem = previousIds.find((previousItem: string): boolean => {
      const findItem = items.find((item: FocusTrackerItemDirective): boolean => item.trackById() === previousItem);
      return findItem === undefined;
    });
    if (removedItem !== undefined) {
      const removedIndex = previousIds.findIndex((item: string): boolean => item === removedItem);
      const newIndex = items.length > removedIndex ? removedIndex : 0;
      items[newIndex]?.focus();
    }
  }
}
